import axios from 'axios';
import * as React from 'react';
import { List } from 'react-virtualized';

// This componenet currently fetches all data
// and does a frontend filter by regex for all types
// other than users;
// TODO: See if it makes sense to implement search logic for
// - groups
// - teams
// TODO: Define TS Types
// TODO: Add Caching Mecahnism when switching between input type

type SearchInputState = {
  data: any[];
  filteredData: any[];
  inputValue: string;
  loading: boolean;
  timeoutId: number;
};

type SearchInputProps = {
  locations?: any[];
  authorizedLocations?: Set<string>;
  onItemSelect: (item: any) => (e: React.MouseEvent) => void;
  selectedIds: Record<string, boolean>;
  type: 'users' | 'locations' | 'groups' | 'teams';
};

const TYPE_ENDPOINT_MAP = {
  users: '/user/org',
  locations: '/org/locations',
  groups: '/group/all',
  teams: '/group/all',
};

class SearchInput extends React.Component<SearchInputProps, SearchInputState> {
  constructor(props: any) {
    super(props);
    this.state = {
      data: [],
      filteredData: [],
      inputValue: '',
      loading: true,
      timeoutId: null,
    };
  }

  componentDidMount() {
    this.setState({ loading: true }, this.initializeData);
  }

  initializeData = async () => {
    const { type, locations, authorizedLocations } = this.props;
    const endpoint = TYPE_ENDPOINT_MAP[type];
    let { data } = await axios.get(endpoint);
    if (this.props.type !== type) return;
    if (type === 'users') data = data.users;
    else if (type === 'locations') {
      const availableLocations = authorizedLocations
        ? locations.reduce((acc: any[], cur: any, index: number) => {
            if (authorizedLocations.has(index.toString())) acc[index] = cur;
            return acc;
          }, [])
        : locations;
      data = availableLocations
        .map((location: any, index: number) => ({ ...location, index }))
        .filter((location: any) => !location.isDeleted);
    } else if (type === 'teams')
      data = data.filter((item: any) => item.type === 'team');
    else if (type === 'groups')
      data = data.filter((item: any) => item.type === 'group');
    this.setState({ data }, this.dataFilterUpdate);
  };

  _getSearchBoolean = (
    type: SearchInputProps['type'],
    regex: RegExp,
    item: any,
  ) => {
    let bool = true;
    switch (type) {
      case 'locations':
      case 'groups':
      case 'teams':
        bool = regex.test(item.name);
        break;
      default:
    }
    return bool;
  };

  search = async () => {
    const { type } = this.props;
    const { inputValue } = this.state;
    let endpoint = TYPE_ENDPOINT_MAP[type];
    if (type === 'users')
      endpoint = `${endpoint}?userName=${inputValue.split(' ').join('+')}`;
    let { data } = await axios.get(endpoint);
    if (type === 'users') data = data.users;
    // TODO: Implement Actual Search for types other than users
    this.setState({ data }, this.dataFilterUpdate);
  };

  debouncedSearch = () => {
    const { timeoutId } = this.state;
    window.clearTimeout(timeoutId);
    const newId = window.setTimeout(this.search, 350);
    this.setState({ timeoutId: newId });
  };

  componentDidUpdate(prevProps: SearchInputProps) {
    const { selectedIds, type } = this.props;
    const { selectedIds: prevSelectedIds, type: prevType } = prevProps;
    if (
      Object.keys(selectedIds).length !== Object.keys(prevSelectedIds).length
    ) {
      this.dataFilterUpdate();
    }
    if (type !== prevType) {
      this.setState(
        { inputValue: '', data: [], filteredData: [], loading: true },
        this.initializeData,
      );
    }
  }

  dataFilterUpdate = () => {
    const { data, inputValue } = this.state;
    const { selectedIds, type } = this.props;
    const regexSet = `(${inputValue.split(' ').join('|')})`;
    const regex = new RegExp(regexSet, 'i');
    const mapField = type === 'locations' ? 'name' : 'id';
    const filteredData = data.filter(
      item =>
        !selectedIds[item[mapField]] &&
        this._getSearchBoolean(type, regex, item),
    );
    this.setState({ filteredData, loading: false });
  };

  onInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { type } = this.props;
    const { value } = event.currentTarget;
    const cb = type === 'users' ? this.debouncedSearch : this.dataFilterUpdate;
    this.setState({ inputValue: value }, cb);
  };

  _noRowsRenderer = () => {
    const { type } = this.props;
    const { loading } = this.state;
    return !loading && <div className="row-details">{`No ${type} found`}</div>;
  };

  _rowRenderer: (options: any) => any = ({
    index, // Index of row
    isScrolling, // The List is currently being scrolled
    isVisible, // This row is visible within the List (eg it is not an overscanned row)
    key, // Unique key within array of rendered rows
    parent, // Reference to the parent List (instance)
    style, // Style object to be applied to row (to position it);
    // This must be passed through to the rendered row element.
  }) => {
    const { onItemSelect, type } = this.props;
    const { filteredData } = this.state;
    const item = filteredData[index];
    return (
      <div
        key={key}
        style={style}
        onClick={onItemSelect(item)}
        className={
          item.isDeleted ? 'search-input-row disabled' : 'search-input-row'
        }
      >
        {type === 'users' && (
          <div className="row-details">
            <div>{`${item.firstName} ${item.lastName}`}</div>
            {item.role && <small>{item.role.replace('_', ' ')}</small>}
          </div>
        )}
        {type === 'locations' && (
          <div className="row-details">
            <div>{`${item.name}`}</div>
            <small>{`location with ${item.addresses?.length} address(es)`}</small>
          </div>
        )}
        {['groups', 'teams'].includes(type) && (
          <div className="row-details">
            <div>{`${item.name}`}</div>
            <small>{`${item.type} with ${item.userCount} users`}</small>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { filteredData, loading, inputValue } = this.state;
    const { type } = this.props;
    return (
      <div className="search-input-container">
        <div className="search-input input-group">
          <input
            id="search-input-field"
            value={inputValue}
            onChange={this.onInputChange}
            disabled={loading}
            type="text"
            className="form-control"
            placeholder={`Search for ${type}`}
            aria-describedby="basic-addon1"
          />
          <span className="input-group-addon" id="basic-addon1">
            <i
              className={loading ? 'fa fa-spinner fa-spin' : 'fa fa-search'}
            ></i>
          </span>
        </div>
        {filteredData && (
          <List
            className={
              loading ? 'search-input-list loading' : 'search-input-list'
            }
            width={500}
            height={300}
            rowCount={filteredData.length}
            rowHeight={60}
            rowRenderer={this._rowRenderer}
            noRowsRenderer={this._noRowsRenderer}
          />
        )}
      </div>
    );
  }
}

export default SearchInput;
